import {some} from '@danwang/option';
import {
  FeePeriodsByAccountDocument,
  useCreateTransactionMutation,
  useLinkBenefitToRawTransactionMutation,
  type CreateTransactionMutation,
  type FullCardBenefitFragment,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import * as React from 'react';
import {addNewCategory} from 'src/apolloCache/updateCategories';
import {addPayee} from 'src/apolloCache/updatePayees';
import {Button} from 'src/Button';
import {BenefitSummary} from 'src/CreateTransactionForBenefitModal/BenefitSummary';
import {TransactionForm} from 'src/CreateTransactionForBenefitModal/TransactionForm';
import {
  RawTransactionPicker,
  type RawTransactionSelection,
} from 'src/inputs/RawTransactionPicker';
import {Modal} from 'src/Modal';
import type {FetchResult} from '@apollo/client';
import type {DateTime} from 'luxon';
import type {FormState} from 'src/CreateTransactionForBenefitModal/TransactionForm';
import type {SystemProps} from 'src/Modals';

export type FromBenefitProps = {
  benefit: FullCardBenefitFragment;
  feePeriodPayee?: string | null;
  onSuccess: () => void;
};

type BenefitStep =
  | {type: 'pick-transaction'}
  | {type: 'form'; rawTransactionId: string; date: DateTime}
  | {type: 'link-failed'};

export const BenefitFlow = (props: FromBenefitProps & SystemProps) => {
  const {benefit, feePeriodPayee, onSuccess, onRequestClose} = props;
  const formId = React.useId();

  const [step, setStep] = React.useState<BenefitStep>({
    type: 'pick-transaction',
  });
  const [selectedRawTransactionId, setSelectedRawTransactionId] =
    React.useState('');

  const [form, setForm] = React.useState<FormState>({
    payee: feePeriodPayee ?? benefit.name,
    description: benefit.name,
    category: some(benefit.category.name),
  });

  const handleChange =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) => {
      setForm((prev) => ({...prev, [key]: value}));
    };

  const handleRawTransactionChange = (
    rawTransactionId: string,
    transaction?: RawTransactionSelection,
  ) => {
    setSelectedRawTransactionId(rawTransactionId);
    if (transaction) {
      setStep({
        type: 'form',
        rawTransactionId,
        date: parseDate(transaction.date),
      });
    }
  };

  const [createTransaction, {loading}] = useCreateTransactionMutation();
  const [linkBenefit] = useLinkBenefitToRawTransactionMutation();
  const canSubmit = step.type === 'form' && !form.category.isEmpty;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step.type !== 'form' || !canSubmit) return;

    form.category.forEach((category) => {
      void createTransaction({
        variables: {
          rawTransactions: [step.rawTransactionId],
          date: step.date.toISODate(),
          payee: form.payee,
          description: form.description,
          category,
        },
        refetchQueries: [
          {
            query: FeePeriodsByAccountDocument,
            variables: {accountId: benefit.accountId},
          },
        ],
        update: (store, result: FetchResult<CreateTransactionMutation>) => {
          const tx = result?.data?.createTransaction;
          if (tx) {
            tx.category && addNewCategory(store, tx.category);
            tx.payee && addPayee(store, tx.payee);
          }
        },
      }).then(async (response) => {
        if (response.data) {
          try {
            await linkBenefit({
              variables: {
                rawTransactionId: step.rawTransactionId,
                benefitId: benefit._id,
              },
            });
          } catch {
            onSuccess();
            setStep({type: 'link-failed'});
            return;
          }
          onSuccess();
          onRequestClose();
        }
      });
    });
  };

  if (step.type === 'pick-transaction') {
    return (
      <Modal
        onRequestClose={onRequestClose}
        header="Select transaction"
        footer={
          <Button variant="outline" onClick={onRequestClose}>
            Cancel
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <BenefitSummary benefit={benefit} feePeriodPayee={feePeriodPayee} />
          <RawTransactionPicker
            value={selectedRawTransactionId}
            onChange={handleRawTransactionChange}
            includeProcessed={false}
          />
        </div>
      </Modal>
    );
  }

  if (step.type === 'link-failed') {
    return (
      <Modal
        onRequestClose={onRequestClose}
        header="Benefit link failed"
        footer={
          <Button variant="primary" onClick={onRequestClose}>
            Close
          </Button>
        }
      >
        <p className="text-sm text-destructive">
          The transaction was created, but the benefit could not be linked. You
          can link it manually from the raw transaction detail view.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Create transaction with benefit"
      footer={
        <>
          <Button variant="outline" onClick={onRequestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            disabled={loading || !canSubmit}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <BenefitSummary
          benefit={benefit}
          feePeriodPayee={feePeriodPayee}
          onBack={() => setStep({type: 'pick-transaction'})}
        />
        <TransactionForm
          formId={formId}
          form={form}
          onChange={handleChange}
          onSubmit={handleSubmit}
        />
      </div>
    </Modal>
  );
};
